import React, {useCallback, useEffect, useState} from "react";
import {
    AdNetworkDestination,
    AdNetworkEventType,
    AdNetworkTestResult,
    PresenceCondition
} from "../../http/protocol";
import {getContainer} from "../../ioc/IOCSetup";
import {t} from "../../i18n";
import {CallbackLogTable} from "./CallbackLogTable";
import {DestinationForm} from "./DestinationForm";

/** Defaults per the typical setup: require click_id, never double-fire for affiliate-attributed users. */
const newDestination = (): AdNetworkDestination => ({
    enabled: true,
    matchConditions: {clickId: PresenceCondition.PRESENT, ref: PresenceCondition.ABSENT}
});

const MACRO_HELP: { macro: string; key: string; description: string }[] = [
    {macro: "{click_id}", key: "click-id", description: "Click id injected by the ad platform at landing (URL param click_id)"},
    {macro: "{ref}", key: "ref", description: "Affiliate tracker code (URL param ref)"},
    {macro: "{ca}", key: "ca", description: "Campaign (URL param ca)"},
    {macro: "{ch}", key: "ch", description: "Channel (URL param ch)"},
    {macro: "{src}", key: "src", description: "Source (URL param src)"},
    {macro: "{event}", key: "event", description: "Event type: signup or ftd"},
    {macro: "{user_id}", key: "user-id", description: "The user's public id"},
    {macro: "{txid}", key: "txid", description: "Deposit transaction id (FTD only)"},
    {macro: "{amount_eur}", key: "amount-eur", description: "Deposit amount converted to EUR, 2 decimals (FTD only)"}
];

export const AdNetworksView = () => {
    const service = getContainer().getAdNetworkService();

    const [destinations, setDestinations] = useState<AdNetworkDestination[]>([]);
    const [editing, setEditing] = useState<AdNetworkDestination | undefined>();
    const [testResult, setTestResult] = useState<{ destination: string; eventType: AdNetworkEventType; result: AdNetworkTestResult } | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [logRefresh, setLogRefresh] = useState(0);

    const load = useCallback(() => {
        service.listDestinations()
            .then(items => { setDestinations(items); setError(undefined); })
            .catch(() => setError(t("ad-networks.load-error", undefined, "Failed to load ad network destinations")));
    }, [service]);

    useEffect(() => {
        load();
    }, [load]);

    const save = (destination: AdNetworkDestination): Promise<void> => {
        const request = destination.id
            ? service.updateDestination(destination.id, destination)
            : service.createDestination(destination);
        return request.then(() => {
            setEditing(undefined);
            load();
        });
    };

    const remove = (destination: AdNetworkDestination) => {
        const message = t("ad-networks.confirm-delete", undefined, "Delete destination? The delivery log is kept.");
        if (!window.confirm(message + "\n\n" + (destination.name ?? ""))) {
            return;
        }
        service.deleteDestination(destination.id!)
            .then(load)
            .catch(() => setError(t("ad-networks.delete-error", undefined, "Failed to delete destination")));
    };

    const sendTest = (destination: AdNetworkDestination, eventType: AdNetworkEventType) => {
        setTestResult(undefined);
        service.sendTestCallback(destination.id!, eventType)
            .then(result => {
                setTestResult({destination: destination.name ?? "", eventType: eventType, result: result});
                setLogRefresh(token => token + 1);
            })
            .catch(() => setError(t("ad-networks.test-error", undefined, "Failed to send test callback")));
    };

    return (
        <div data-testid="ad-networks-view">
            <h2>{t("ad-networks.title", undefined, "Ad Networks")}</h2>
            <p className="text-muted">
                {t("ad-networks.intro", undefined,
                    "Server-to-server callbacks to ad trackers on signup and first deposit (FTD). " +
                    "Users are matched via the acquisition trackers captured at landing (click_id, ref, ca, ch, src).")}
            </p>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="card-box">
                <h4>{t("ad-networks.destinations-title", undefined, "Destinations")}</h4>
                <button className="btn btn-primary mb-2" data-testid="ad-network-add" onClick={() => setEditing(newDestination())}>
                    {t("ad-networks.add-destination", undefined, "Add destination")}
                </button>
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                        <tr>
                            <th>{t("ad-networks.col-id", undefined, "ID")}</th>
                            <th>{t("ad-networks.col-name", undefined, "Name")}</th>
                            <th>{t("ad-networks.col-enabled", undefined, "Enabled")}</th>
                            <th>{t("ad-networks.col-signup", undefined, "Signup")}</th>
                            <th>{t("ad-networks.col-ftd", undefined, "FTD")}</th>
                            <th>{t("ad-networks.col-actions", undefined, "Actions")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {destinations.map(destination => (
                            <tr key={destination.id} data-testid="ad-network-destination-row">
                                <td>{destination.id}</td>
                                <td>{destination.name}</td>
                                <td>{destination.enabled ? t("status.yes", undefined, "Yes") : t("status.no", undefined, "No")}</td>
                                <td>{destination.urlTemplateSignup ? t("status.yes", undefined, "Yes") : "—"}</td>
                                <td>{destination.urlTemplateFtd ? t("status.yes", undefined, "Yes") : "—"}</td>
                                <td>
                                    <button className="btn btn-sm btn-primary mr-1" data-testid="ad-network-edit"
                                            onClick={() => setEditing(destination)}>{t("button.edit", undefined, "Edit")}</button>
                                    <button className="btn btn-sm btn-secondary mr-1" data-testid="ad-network-test-signup-btn"
                                            disabled={!destination.urlTemplateSignup}
                                            onClick={() => sendTest(destination, AdNetworkEventType.SIGNUP)}>{t("ad-networks.test-signup", undefined, "Test signup")}</button>
                                    <button className="btn btn-sm btn-secondary mr-1" data-testid="ad-network-test-ftd-btn"
                                            disabled={!destination.urlTemplateFtd}
                                            onClick={() => sendTest(destination, AdNetworkEventType.FTD)}>{t("ad-networks.test-ftd", undefined, "Test FTD")}</button>
                                    <button className="btn btn-sm btn-danger" data-testid="ad-network-delete-btn"
                                            onClick={() => remove(destination)}>{t("button.delete", undefined, "Delete")}</button>
                                </td>
                            </tr>
                        ))}
                        {destinations.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-muted">{t("ad-networks.no-destinations", undefined, "No destinations configured")}</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {testResult && (
                    <div className={"alert " + (testResult.result.responseCode && testResult.result.responseCode < 300 ? "alert-success" : "alert-warning")}
                         data-testid="ad-network-test-result">
                        <strong>{t("ad-networks.test-result", undefined, "Test callback")} ({testResult.eventType} → {testResult.destination}):</strong>{" "}
                        {testResult.result.status}
                        {testResult.result.skipReason && <span> — {testResult.result.skipReason}</span>}
                        {testResult.result.responseCode != null && <span> — HTTP {testResult.result.responseCode}</span>}
                        {testResult.result.finalUrl && <div><small>URL: {testResult.result.finalUrl}</small></div>}
                        {testResult.result.responseBody && <div><small>{t("ad-networks.test-response", undefined, "Response")}: {testResult.result.responseBody}</small></div>}
                    </div>
                )}
            </div>

            {editing && (
                <DestinationForm initial={editing} onSave={save} onCancel={() => setEditing(undefined)}/>
            )}

            <CallbackLogTable service={service} refreshToken={logRefresh}/>

            <div className="card-box">
                <h4>{t("ad-networks.macros-title", undefined, "URL template macros")}</h4>
                <p className="text-muted">
                    {t("ad-networks.macros-doc-intro", undefined, "To read more about tracker parameters to Nano see")}{" "}
                    <a href="https://docs.cubeia.com/docs/trackers" target="_blank" rel="noopener noreferrer">
                        {t("ad-networks.macros-doc-link", undefined, "the tracker documentation")}
                    </a>.
                </p>
                <table className="table table-sm">
                    <tbody>
                    {MACRO_HELP.map(entry => (
                        <tr key={entry.macro}>
                            <td><code>{entry.macro}</code></td>
                            <td>{t("ad-networks.macro." + entry.key, undefined, entry.description)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                <p className="text-muted">
                    {t("ad-networks.macros-example", undefined, "Example")}:{" "}
                    <code>{"https://trk.example.com/postback?cid={click_id}&payout={amount_eur}&txid={txid}&et={event}"}</code>
                </p>
            </div>
        </div>
    );
};
